import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import axios from "../utils/api";

const SaveRobotDetails = () => {
  const [robotDetails, setRobotDetails] = useState({
    robotId: "",
    emailId: "",
    username: "",
    model: "",
    serialNumber: "",
    IPAddress: "",
    image: "",
    status: "active",
    location: "",
    subLocation: "",
    amps: "",
    robotInitializeDate: "",
    lastMaintenanceDate: "",
  });

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [userEmails, setUserEmails] = useState([]);
  const [locationNames, setLocationNames] = useState([]);
  const [subLocations, setSubLocations] = useState([]);
  const navigate = useNavigate();

  // Fetch user emails from the API
  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await axios.get("/get-user-emails", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setUserEmails(response.data);
      } catch (error) {
        console.error("Error fetching user emails:", error);
      }
    };

    fetchEmails();
  }, []);

  // Fetch location names by email
  const fetchLocationNames = async (email) => {
    try {
      const response = await axios.get(`/getuser/location-names/${email}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setLocationNames(
        Array.isArray(response.data.locationNames)
          ? response.data.locationNames
          : []
      );
    } catch (error) {
      console.error("Error fetching location names:", error);
      setLocationNames([]);
    }
  };

  // Fetch sub-locations based on location and email
  const fetchSubLocations = async (email, locationName) => {
    try {
      const response = await axios.get(
        `/user/${email}/location/${locationName}/sublocations`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setSubLocations(
        Array.isArray(response.data.subLocations)
          ? response.data.subLocations
          : []
      );
    } catch (error) {
      console.error("Error fetching sub-locations:", error);
      setSubLocations([]);
    }
  };

  const fetchUserName = async (email) => {
    try {
      const response = await axios.get(`/user-name/${email}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRobotDetails((prevState) => ({
        ...prevState,
        username: response.data.name,
      }));
    } catch (error) {
      console.error("Error fetching user name:", error);
    }
  };

  const fetchModelImage = async (modelName) => {
    try {
      const response = await axios.get(`/ModelImage/${modelName}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRobotDetails((prevState) => ({
        ...prevState,
        image: response.data.image,
      }));
    } catch (error) {
      console.error("Error fetching model image:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "emailId") {
      fetchUserName(value);
      fetchLocationNames(value);
      setRobotDetails((prevState) => ({
        ...prevState,
        emailId: value,
        location: "",
        subLocation: "",
      }));
    } else if (name === "location") {
      fetchSubLocations(robotDetails.emailId, value);
    } else if (name === "model") {
      fetchModelImage(value);
    }

    setRobotDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const clearForm = () => {
    setRobotDetails({
      robotId: "",
      emailId: "",
      username: "",
      model: "",
      serialNumber: "",
      IPAddress: "",
      image: "",
      status: "active",
      location: "",
      subLocation: "",
      amps: "",
      robotInitializeDate: "",
      lastMaintenanceDate: "",
    });
  };

  const validate = () => {
    let tempErrors = {};
    if (!robotDetails.robotId) tempErrors.robotId = "Robot ID is required";
    if (!robotDetails.emailId) tempErrors.emailId = "Email ID is required";
    if (!robotDetails.username) tempErrors.username = "Username is required";
    if (!robotDetails.model) tempErrors.model = "Model is required";
    if (!robotDetails.serialNumber)
      tempErrors.serialNumber = "Serial Number is required";
    if (!robotDetails.IPAddress)
      tempErrors.IPAddress = "IP Address is required";
    if (!robotDetails.image) tempErrors.image = "Image URL is required";
    if (!robotDetails.location) tempErrors.location = "Location is required";
    if (!robotDetails.subLocation)
      tempErrors.subLocation = "Sub-Location is required";
    if (!robotDetails.robotInitializeDate)
      tempErrors.robotInitializeDate = "Initialize Date is required";
    if (!robotDetails.lastMaintenanceDate)
      tempErrors.lastMaintenanceDate = "Maintenance Date is required";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const response = await axios.post("/robot", robotDetails, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setSnackbar({
        open: true,
        message: response.data.message,
        severity: "success",
      });
      clearForm();
      setTimeout(() => {
        navigate("/user-robot");
      }, 3000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error: ${
          error.response ? error.response.data.error : error.message
        }`,
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <div style={containerStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={backButtonStyle}
      >
        <ArrowBackIcon />
      </IconButton>
      
      <div style={headerContainerStyle}>
        <h1 style={headerStyle}>Create New Robot</h1>
        <p style={subHeaderStyle}>For User: {robotDetails.username || "Not selected"}</p>
      </div>

      <div style={contentContainerStyle}>
        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGridStyle}>
            {/* Column 1 */}
            <div style={formColumnStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Email ID*</label>
                <select
                  name="emailId"
                  value={robotDetails.emailId}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select Email</option>
                  {userEmails.map((email) => (
                    <option key={email} value={email}>
                      {email}
                    </option>
                  ))}
                </select>
                {errors.emailId && <span style={errorStyle}>{errors.emailId}</span>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Robot ID*</label>
                <input
                  type="text"
                  name="robotId"
                  value={robotDetails.robotId}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Enter unique robot ID"
                />
                {errors.robotId && <span style={errorStyle}>{errors.robotId}</span>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Username</label>
                <input
                  type="text"
                  name="username"
                  value={robotDetails.username}
                  onChange={handleChange}
                  style={{...inputStyle, backgroundColor: "#f5f5f5"}}
                  readOnly
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Model*</label>
                <select
                  name="model"
                  value={robotDetails.model}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select Model</option>
                  <option value="Spotbot-Lite">Spotbot-Lite</option>
                  <option value="Spotbot-Xtreme">Spotbot-Xtreme</option>
                </select>
                {errors.model && <span style={errorStyle}>{errors.model}</span>}
              </div>
            </div>

            {/* Column 2 */}
            <div style={formColumnStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Serial Number*</label>
                <input
                  type="text"
                  name="serialNumber"
                  value={robotDetails.serialNumber}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Enter serial number"
                />
                {errors.serialNumber && <span style={errorStyle}>{errors.serialNumber}</span>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>IP Address*</label>
                <input
                  type="text"
                  name="IPAddress"
                  value={robotDetails.IPAddress}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Enter IP address"
                />
                {errors.IPAddress && <span style={errorStyle}>{errors.IPAddress}</span>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Location*</label>
                <select
                  name="location"
                  value={robotDetails.location}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select Location</option>
                  {locationNames.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                {errors.location && <span style={errorStyle}>{errors.location}</span>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Sub-Location*</label>
                <select
                  name="subLocation"
                  value={robotDetails.subLocation}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select Sub-Location</option>
                  {subLocations.map((subLocation) => (
                    <option key={subLocation} value={subLocation}>
                      {subLocation}
                    </option>
                  ))}
                </select>
                {errors.subLocation && <span style={errorStyle}>{errors.subLocation}</span>}
              </div>
            </div>

            {/* Column 3 */}
            <div style={formColumnStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Amps</label>
                <input
                  type="text"
                  name="amps"
                  value={robotDetails.amps}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Enter amps value"
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Initialize Date*</label>
                <input
                  type="date"
                  name="robotInitializeDate"
                  value={robotDetails.robotInitializeDate}
                  onChange={handleChange}
                  style={inputStyle}
                />
                {errors.robotInitializeDate && <span style={errorStyle}>{errors.robotInitializeDate}</span>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Maintenance Date*</label>
                <input
                  type="date"
                  name="lastMaintenanceDate"
                  value={robotDetails.lastMaintenanceDate}
                  onChange={handleChange}
                  style={inputStyle}
                />
                {errors.lastMaintenanceDate && <span style={errorStyle}>{errors.lastMaintenanceDate}</span>}
              </div>
            </div>
          </div>

          <button type="submit" style={submitButtonStyle}>
            Save Robot
          </button>
        </form>

        <div style={imageContainerStyle}>
          {robotDetails.image ? (
            <div style={imageWrapperStyle}>
              <img 
                src={robotDetails.image} 
                alt="Robot Model" 
                style={imageStyle} 
              />
              <p style={imageCaptionStyle}>{robotDetails.model || "No model selected"}</p>
            </div>
          ) : (
            <div style={placeholderStyle}>
              <p style={placeholderTextStyle}>Preview will appear here</p>
              <p style={placeholderSubTextStyle}>Select a model to view image</p>
            </div>
          )}
        </div>
      </div>

      {snackbar.open && (
        <div style={snackbarStyle(snackbar.severity)}>
          {snackbar.message}
          <button onClick={handleCloseSnackbar} style={closeButtonStyle}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle = {
  width: "95vw",
  maxWidth: "95vw",
  margin: 0,
  padding: "28px 24px 88px",
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  background:
    "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  minHeight: "100vh",
};

const backButtonStyle = {
  position: "absolute",
  top: "20px",
  left: "20px",
  backgroundColor: "white",
  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  color: "#555",
  "&:hover": {
    backgroundColor: "#f0f0f0",
  },
};

const headerContainerStyle = {
  textAlign: "center",
  marginBottom: "2rem",
  paddingBottom: "1rem",
  borderBottom: "1px solid #e0e0e0",
};

const headerStyle = {
  fontSize: "2rem",
  fontWeight: "600",
  color: "#333",
  marginBottom: "0.5rem",
};

const subHeaderStyle = {
  fontSize: "1rem",
  color: "#666",
  fontStyle: "italic",
};

const contentContainerStyle = {
  display: "flex",
  gap: "2rem",
  alignItems: "flex-start",
};

const formStyle = {
  flex: 2,
  backgroundColor: "white",
  padding: "2rem",
  borderRadius: "8px",
  boxShadow: "0 2px 15px rgba(0,0,0,0.05)",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "1.5rem",
  marginBottom: "2rem",
};

const formColumnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
};

const formGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const labelStyle = {
  fontSize: "0.9rem",
  fontWeight: "500",
  color: "#444",
};

const inputStyle = {
  padding: "0.75rem",
  border: "1px solid #ddd",
  borderRadius: "4px",
  fontSize: "0.9rem",
  transition: "border 0.3s ease",
  ":focus": {
    outline: "none",
    borderColor: "#4a90e2",
    boxShadow: "0 0 0 2px rgba(74,144,226,0.2)",
  },
};

const errorStyle = {
  color: "#e74c3c",
  fontSize: "0.8rem",
  marginTop: "0.25rem",
};

const submitButtonStyle = {
  width: "100%",
  padding: "0.75rem",
  backgroundColor: "#4a90e2",
  color: "white",
  border: "none",
  borderRadius: "4px",
  fontSize: "1rem",
  fontWeight: "500",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
  ":hover": {
    backgroundColor: "#3a80d2",
  },
};

const imageContainerStyle = {
  flex: 1,
  position: "sticky",
  top: "20px",
};

const imageWrapperStyle = {
  backgroundColor: "white",
  padding: "1.5rem",
  borderRadius: "8px",
  boxShadow: "0 2px 15px rgba(0,0,0,0.05)",
  textAlign: "center",
};

const imageStyle = {
  width: "100%",
  maxHeight: "300px",
  objectFit: "contain",
  marginBottom: "1rem",
  borderRadius: "4px",
};

const imageCaptionStyle = {
  fontSize: "1rem",
  fontWeight: "500",
  color: "#333",
};

const placeholderStyle = {
  backgroundColor: "#f5f5f5",
  padding: "2rem",
  borderRadius: "8px",
  textAlign: "center",
  height: "300px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  border: "2px dashed #ddd",
};

const placeholderTextStyle = {
  fontSize: "1rem",
  color: "#888",
  marginBottom: "0.5rem",
};

const placeholderSubTextStyle = {
  fontSize: "0.8rem",
  color: "#aaa",
};

const snackbarStyle = (severity) => ({
  position: "fixed",
  bottom: "20px",
  right: "20px",
  backgroundColor: severity === "success" ? "#4CAF50" : "#F44336",
  color: "white",
  padding: "1rem 1.5rem",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
  animation: "fadeIn 0.3s ease",
});

const closeButtonStyle = {
  background: "none",
  border: "none",
  color: "white",
  fontSize: "1rem",
  cursor: "pointer",
  marginLeft: "0.5rem",
};

export default SaveRobotDetails;